/*
Copyright 2020 Adobe
All Rights Reserved.
NOTICE: Adobe permits you to use, modify, and distribute this file in
accordance with the terms of the Adobe license agreement accompanying
it. If you have received this file from a source other than Adobe,
then your use, modification, or distribution of it requires the prior
written permission of Adobe.
*/
import { Injectable, isDevMode } from '@angular/core';
import 'zone.js/dist/zone';
import { HelperService } from './helper.service';
import { serviceConstants } from '../constants/service.constants';
//import { AppConsts } from 'src/app/models/app-consts';

declare global {
  interface Window {
    AdobeDC: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AdobePdfViewerService {

  // Credential details of PDF Embed API
  // API KEY generated at: https://console.adobe.io/projects/389453/4566206088344577390/apis/ViewSDK/details#
  // or https://developer.adobe.com/console/projects/756716/4566206088344717499/apis/ViewSDK/details
  clientID = serviceConstants.AdobeViewerClientID;
  //AppConsts.AdobeViewerClientID;

  // ALLOWED DOMAIN: thscloud.com
  // prodClientID = 'cf628fb956c8481f9afe42e17b4add6c'; // This is used for running under *.thscloud.com domain
  // devClientID = '8c0cd670273d451cbc9b351b11d22318'; // This is used for running under localhost domain

  adobeDCView: any;

  readyPromise: Promise<any> = new Promise((resolve) => {
    if (window.AdobeDC) {
      resolve('');
    } else {
      /* Wait for Adobe Document Services PDF Embed API to be ready */
      document.addEventListener('adobe_dc_view_sdk.ready', () => { resolve(''); });
    }
  });

  ready() {
    return this.readyPromise;
  }

  previewFile(divId: string,
    fileURI: string,
    fileName: string = ' ',
    adobeControls: Partial<AdobeControls> = {
      showAnnotationTools: false,
      showDownloadPDF: false,
      showLeftHandPanel: false,
      showPageControls: false,
      showPrintPDF: false
    }) {
debugger
    let defControls: AdobeControls = {
      showAnnotationTools: false,
      showDownloadPDF: false,
      showLeftHandPanel: false,
      showPageControls: false,
      showPrintPDF: false
    };

    defControls = HelperService.extend(defControls, adobeControls)

    if (isDevMode())
      console.log('Running in DEV mode: ' + this.clientID)
    else
      console.log('Running in PROD mode: ' + this.clientID);

    const config: any = {
      /* Pass your registered client id */
      clientId: this.clientID
    };
    if (divId) { /* Optional only for Light Box embed mode */
      /* Pass the div id in which PDF should be rendered */
      config.divId = divId;
    }
    debugger
    /* Initialize the AdobeDC View object */
    this.adobeDCView = new window.AdobeDC.View(config);

    /* Invoke the file preview API on Adobe DC View object */
    const previewFilePromise = this.adobeDCView.previewFile({
      /* Pass information on how to access the file */
      content: {
        /* Location of file where it is hosted */
        location: {
          url: fileURI, /*'https://documentcloud.adobe.com/view-sdk-demo/PDFs/Bodea Brochure.pdf',*/
          /*
          If the file URL requires some additional headers, then it can be passed as follows:-
          headers: [
            {
              key: '<HEADER_KEY>',
              value: '<HEADER_VALUE>',
            }
          ]
          */
        },
      },
      /* Pass meta data of file */
      metaData: {
        /* file name */
        fileName: fileName,
        /* file ID, A Unique ID */
        id: HelperService.GetUniqueKey()
      }
    }, {
      showAnnotationTools: defControls.showAnnotationTools,
      showLeftHandPanel: defControls.showLeftHandPanel,
      showPageControls: defControls.showPageControls,
      showDownloadPDF: defControls.showDownloadPDF,
      showPrintPDF: defControls.showPrintPDF
    });

    return previewFilePromise;
  }

  previewFileUsingFilePromise(divId: string, filePromise: Promise<string | ArrayBuffer>, fileName: any) {
    /* Initialize the AdobeDC View object */
    this.adobeDCView = new window.AdobeDC.View({
      /* Pass your registered client id */
      clientId: this.clientID,
      /* Pass the div id in which PDF should be rendered */
      divId,
    });

    /* Invoke the file preview API on Adobe DC View object */
    this.adobeDCView.previewFile({
      /* Pass information on how to access the file */
      content: {
        /* pass file promise which resolve to arrayBuffer */
        promise: filePromise,
      },
      /* Pass meta data of file */
      metaData: {
        /* file name */
        fileName
      }
    }, {});
  }

  registerSaveApiHandler() {
    /* Define Save API Handler */
    const saveApiHandler = (metaData: any, content: any, options: any) => {
     // console.log(metaData, content, options);
      return new Promise((resolve) => {
        /* Dummy implementation of Save API, replace with your business logic */
        setTimeout(() => {
          const response = {
            code: window.AdobeDC.View.Enum.ApiResponseCode.SUCCESS,
            data: {
              metaData: Object.assign(metaData, { updatedAt: new Date().getTime() })
            },
          };
          resolve(response);
        }, 2000);
      });
    };

    this.adobeDCView.registerCallback(
      window.AdobeDC.View.Enum.CallbackType.SAVE_API,
      saveApiHandler,
      {}
    );
  }

  registerEventsHandler() {
    /* Register the callback to receive the events */
    this.adobeDCView.registerCallback(
      /* Type of call back */
      window.AdobeDC.View.Enum.CallbackType.EVENT_LISTENER,
      /* call back function */
      (event: any) => {
       /// console.log(event);
      },
      /* options to control the callback execution */
      {
        /* Enable PDF analytics events on user interaction. */
        enablePDFAnalytics: true,
      }
    );
  }
}

export interface AdobeControls {
  showAnnotationTools: boolean;
  showLeftHandPanel: boolean;
  showPageControls: boolean;
  showDownloadPDF: boolean;
  showPrintPDF: boolean;
}

